import { useState } from "react";
import { useDispatch } from "react-redux";
import useRecipients from "./useRecipients";
import useSenders from "./useSenders";
import instance from "../Api/Instance";
import { modelPreregistrationData } from "../utils/preRegistrationUtils";
import {
  getErrorMessage,
  getPreRegisterUrl,
  processParticipantSaveResult,
  processSuccessfulResponse,
  processErrorResponse,
  processCatchError,
} from "../utils/preRegisterService";

const initialPreRegisterDetailsState = {
  isLoading: false,
  data: [],
  errors: [],
};

const checkIfMassive = (details) => !!details?.massivePreRegistration;
const checkIfHasFile = (details) => !!details?.file;
const checkIfProximity = (details) =>
  details?.serviceType.includes("Proximidad");

const getFileType = (preRegisterDetails) => {
  const serviceType = preRegisterDetails?.serviceType;
  const productTypeKey = preRegisterDetails?.productType?.key;

  if (serviceType === "Frio Proximidad") return 3;

  const productTypeMap = {
    "FRAXQ-DOUAOF": 1,
    "FRAXS-DOUAOF": 2,
    "PADXA-DOUAOF": 4,
  };

  return productTypeMap[productTypeKey];
};

export default function usePreregister() {
  const dispatch = useDispatch();
  const { addSender } = useSenders();
  const { addRecipient } = useRecipients();
  const [preRegistrationDetails, setPreRegistrationDetails] = useState(
    initialPreRegisterDetailsState
  );

  const updatePreRegistrationDetails = (details) =>
    setPreRegistrationDetails({
      ...initialPreRegisterDetailsState,
      ...details,
    });

  const appendError = (error) =>
    setPreRegistrationDetails((prev) => ({
      ...prev,
      errors: [...prev.errors, error],
    }));

  const saveParticipantIfNeeded = async ({
    type,
    participant,
    addFn,
    setFieldsValue,
  }) => {
    const needsSave = !participant?.participantId && participant?.save;
    if (!needsSave) return participant?.participantId ?? null;

    const result = await addFn(participant, false);

    if (result?.status === "error") {
      appendError(result.error);
      return null;
    }

    if (result?.status === "ok") {
      return processParticipantSaveResult({
        type,
        participant,
        result,
        setFieldsValue,
        dispatch,
      });
    }

    return null;
  };

  const executePreRegister = async (
    details,
    url,
    errorMsg,
    coldchainParam,
    isMassive
  ) => {
    try {
      const config =
        coldchainParam !== undefined && !isMassive
          ? { params: { coldchain: coldchainParam } }
          : {};

      const response = await instance.post(url, details, config);
      return response.status === 201
        ? processSuccessfulResponse(
            response.data,
            url,
            updatePreRegistrationDetails
          )
        : processErrorResponse([errorMsg], updatePreRegistrationDetails);
    } catch (error) {
      console.error(error);
      return processCatchError(error, updatePreRegistrationDetails);
    }
  };

  const handlePreRegisterSteps = async (preRegisterDetails, setFieldsValue) => {
    updatePreRegistrationDetails({ isLoading: true });

    const refined = modelPreregistrationData(preRegisterDetails);
    const isMassive = checkIfMassive(preRegisterDetails);
    const hasFile = checkIfHasFile(preRegisterDetails);
    const isProximity = checkIfProximity(preRegisterDetails);
    const isColdchain = preRegisterDetails?.coldchain ?? false;
    const coldchainParam = !isProximity ? isColdchain : undefined;
    const fileType = getFileType(preRegisterDetails);

    const senderId = await saveParticipantIfNeeded({
      type: "sender",
      participant: refined.sender,
      addFn: addSender,
      setFieldsValue,
    });
    if (senderId) refined.sender.participantId = senderId;

    const shouldSaveRecipient = !(isMassive && hasFile);
    if (shouldSaveRecipient) {
      const recipientId = await saveParticipantIfNeeded({
        type: "recipient",
        participant: refined.addressee,
        addFn: addRecipient,
        setFieldsValue,
      });
      if (recipientId) refined.addressee.participantId = recipientId;
    }

    const url = getPreRegisterUrl(isMassive && hasFile, isProximity, fileType);
    const errorMsg = getErrorMessage(isMassive, hasFile);

    return await executePreRegister(
      refined,
      url,
      errorMsg,
      coldchainParam,
      isMassive && hasFile
    );
  };

  return {
    handlePreRegisterSteps,
    preRegistrationDetails,
  };
}
